import {
  PLAN_KEYS,
  type ConsoleOrganization,
  type OrganizationLimits,
  type PlanKey,
} from '@sentinel/shared';
import { Button, InputField, Panel, SelectField } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsoleCan } from '@/auth/use-console-can';
import type { useOrganization } from './use-organization';

const K = 'console.organization';
const LIMITS = ['maxUsers', 'maxActiveMissions', 'maxDevices', 'storageMb'] as const;
const WHOLE_NUMBER = /^[1-9]\d*$/;

type Props = { organization: ConsoleOrganization } & Pick<
  ReturnType<typeof useOrganization>,
  'setPlan' | 'setLimits'
>;

/** The contract: a package as starting template (its modules available, its limits copied),
 *  then limits of the organization's own. An empty limit is «no limit». */
export function PlanLimitsPanel({ organization, setPlan, setLimits }: Props) {
  const { t } = useTranslation();
  const canPlan = useConsoleCan('console.entitlements:manage');
  const canLimits = useConsoleCan('console.organizations:update');
  const [plan, setPlanKey] = useState<PlanKey>(organization.planKey ?? 'operational');
  const [limits, setLimitValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(LIMITS.map((k) => [k, organization.limits[k]?.toString() ?? ''])),
  );
  const [invalid, setInvalid] = useState<string[]>([]);

  const saveLimits = () => {
    const bad = LIMITS.filter(
      (k) => (limits[k] ?? '') !== '' && !WHOLE_NUMBER.test(limits[k] ?? ''),
    );
    setInvalid([...bad]);
    if (bad.length > 0) return;
    const next = Object.fromEntries(
      LIMITS.map((k) => [k, (limits[k] ?? '') === '' ? null : Number(limits[k])]),
    ) as OrganizationLimits;
    setLimits.mutate(next);
  };

  return (
    <Panel title={t(`${K}.contract`)}>
      <div className="stack">
        <SelectField
          id="org-plan"
          label={t(`${K}.plan`)}
          hint={t(`${K}.planHint`)}
          value={plan}
          disabled={!canPlan}
          onChange={(e) => setPlanKey(e.target.value as PlanKey)}
          options={PLAN_KEYS.map((p) => ({ value: p, label: t(`console.plans.${p}`) }))}
        />
        {canPlan ? (
          <Button
            size="sm"
            style={{ alignSelf: 'flex-start' }}
            disabled={setPlan.isPending || plan === organization.planKey}
            onClick={() => setPlan.mutate(plan)}
          >
            {t(`${K}.assignPlan`)}
          </Button>
        ) : null}
        {LIMITS.map((k) => (
          <InputField
            key={k}
            id={`limit-${k}`}
            label={t(`${K}.limits.${k}`)}
            inputMode="numeric"
            placeholder={t(`${K}.unlimited`)}
            value={limits[k] ?? ''}
            disabled={!canLimits}
            error={invalid.includes(k) ? t(`${K}.limitInvalid`) : undefined}
            onChange={(e) => setLimitValues({ ...limits, [k]: e.target.value.trim() })}
          />
        ))}
        {canLimits ? (
          <Button
            size="sm"
            style={{ alignSelf: 'flex-start' }}
            disabled={setLimits.isPending}
            onClick={saveLimits}
          >
            {t(`${K}.saveLimits`)}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}
